import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, gt } from 'ember-truth-helpers';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { FileWithValidation } from 'lumen/components/general/file-drop-zone';
import GeneralFileDropZone from 'lumen/components/general/file-drop-zone';
import SettingsBudgetForecastUploadDownloadModalCompleteUpload from 'lumen/components/settings/budget-forecast/upload-download-modal/complete-upload';
import SettingsBudgetForecastUploadDownloadModalDownloadTemplate from 'lumen/components/settings/budget-forecast/upload-download-modal/download-template';
import SettingsBudgetForecastUploadDownloadModalMethodSection from 'lumen/components/settings/budget-forecast/upload-download-modal/method-section';
import type { BudgetForecastEntry } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  DATE_GRANULARITY,
  type DateGranularity,
  DEFAULT_DISTRIBUTION_METHOD,
  type DistributionMethod,
} from 'lumen/utils/constants/budget-forecast-constants';
import type { FileType } from 'lumen/utils/constants/file-types';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './upload-download-modal.module.css';

export type Method = 'download' | 'upload';
export type DownloadOptions = {
  endDate: Dayjs;
  granularity: DateGranularity;
  includeSegments: boolean;
  startDate: Dayjs;
};
export type UploadOptions = {
  distributionMethod: DistributionMethod;
  file: File | null;
  isTooManyFiles: boolean;
  isValid: boolean;
};

type Step =
  | 'select-method'
  | 'download-template'
  | 'upload-file'
  | 'complete-upload';

const DOWNLOAD_STEPS: Step[] = ['select-method', 'download-template'];
const UPLOAD_STEPS: Step[] = [
  'select-method',
  'upload-file',
  'complete-upload',
];

type UploadTemplateQueryParams = {
  distribution_method: DistributionMethod;
  entry_id: string;
  subscription_id: string;
};

type DownloadTemplateQueryParams = {
  date_aggregation: DateGranularity;
  end_date: string;
  entry_id: string;
  start_date: string;
  subscription_id: string;
  upload_per_segment: 'true' | 'false';
};

interface SettingsBudgetForecastUploadDownloadModalSignature {
  Args: {
    entry: BudgetForecastEntry;
    onClose: (wereChangesMade?: boolean) => void;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastUploadDownloadModal extends Component<SettingsBudgetForecastUploadDownloadModalSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Store;
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked downloadOptions: DownloadOptions = {
    endDate: dayjs().endOf('year'),
    granularity: DATE_GRANULARITY.DAY,
    startDate: dayjs().startOf('year'),
    includeSegments: false,
  };

  @tracked method: Method = 'download';
  @tracked stepIndex: number = 0;
  @tracked uploadOptions: UploadOptions = {
    file: null,
    isValid: false,
    distributionMethod: DEFAULT_DISTRIBUTION_METHOD,
    isTooManyFiles: false,
  };

  allowedFormats: FileType[] = ['xlsx'];

  uploadTemplateTask = task({ drop: true }, async (): Promise<void> => {
    const { view, onClose } = this.args;
    const isConfirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('budget_forecast.upload.alert.title'),
      text:
        view === 'budget'
          ? this.intl.t('budget_forecast.upload_budget.alert.description')
          : this.intl.t('budget_forecast.upload_forecast.alert.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t('general.confirm'),
        },
      },
    });

    if (!isConfirmed) {
      return;
    }

    const uploadURL = new URL(
      `/apigateway/v1/app/budgetforecast/${this.args.view}/upload/`,
      this.ajax.host,
    );

    const params: UploadTemplateQueryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id!,
      distribution_method: this.uploadOptions.distributionMethod,
      entry_id: this.args.entry.id!,
    };

    uploadURL.search = new URLSearchParams(params).toString();

    const formData = new FormData();
    formData.append('file', this.uploadOptions.file as Blob);

    try {
      const uploadResponse = await this.ajax.request(uploadURL.toString(), {
        method: 'POST',
        data: formData,
        contentType: false,
        processData: false,
        mimeType: 'multipart/form-data',
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('budget-forecast/version', uploadResponse);

      this.toast.success(
        view === 'budget'
          ? this.intl.t('budget_forecast.upload_budget.upload_success')
          : this.intl.t('budget_forecast.upload_forecast.upload_success'),
        {
          showIcon: true,
          dismissible: true,
        },
      );

      onClose(true);
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        view === 'budget'
          ? this.intl.t('budget_forecast.upload_budget.upload_failed')
          : this.intl.t('budget_forecast.upload_forecast.upload_failed'),
        {
          showIcon: true,
          dismissible: true,
        },
      );

      onClose();
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get buttonProperties(): {
    iconAfter?: IconName;
    isDisabled: boolean;
    isLoading: boolean;
    label: string;
  } {
    const STEP_LABELS: Record<Step, string> = {
      'select-method': this.intl.t('general.next'),
      'download-template': this.intl.t('budget_forecast.download_template'),
      'upload-file': this.intl.t('budget_forecast.upload_and_distribute'),
      'complete-upload': this.intl.t('budget_forecast.upload_and_distribute'),
    };

    const STEP_ICONS: Partial<Record<Step, IconName>> = {
      'select-method': 'arrow-right',
    };

    const STEP_DISABLED: Partial<Record<Step, boolean>> = {
      'upload-file': true,
      'complete-upload':
        !this.uploadOptions.isValid || this.uploadTemplateTask.isRunning,
    };

    return {
      label: STEP_LABELS[this.currentView],
      iconAfter: STEP_ICONS[this.currentView],
      isDisabled: STEP_DISABLED[this.currentView] ?? false,
      isLoading:
        this.uploadTemplateTask.isRunning &&
        this.currentView === 'complete-upload',
    };
  }

  get currentView(): Step {
    return this.steps[this.stepIndex]!;
  }

  get modalTitle(): string {
    const base =
      this.args.view === 'budget'
        ? this.intl.t('budget_forecast.upload_budget.title')
        : this.intl.t('budget_forecast.upload_forecast.title');

    const additional =
      this.currentView === 'download-template'
        ? this.intl.t('budget_forecast.download_template')
        : null;

    return [base, additional].filter(Boolean).join(' - ');
  }

  get steps(): Step[] {
    return this.method === 'download' ? DOWNLOAD_STEPS : UPLOAD_STEPS;
  }

  @action
  closeModal(): void {
    if (this.uploadTemplateTask.isRunning) {
      return;
    }

    this.args.onClose();
  }

  @action
  downloadTemplate(): void {
    const { downloadOptions, hotelAndMarketService, ajax } = this;
    const { view, onClose } = this.args;

    const baseUrl =
      view === 'budget'
        ? `/apigateway/v1/app/budgetforecast/budget/template/`
        : `/apigateway/v1/app/budgetforecast/forecast/template/`;

    const downloadUrl = new URL(baseUrl, ajax.host);

    const queryParams: DownloadTemplateQueryParams = {
      subscription_id: hotelAndMarketService.hotel.id!,
      date_aggregation: downloadOptions.granularity,
      start_date: downloadOptions.startDate.format('YYYY-MM-DD'),
      end_date: downloadOptions.endDate.format('YYYY-MM-DD'),
      upload_per_segment: downloadOptions.includeSegments ? 'true' : 'false',
      entry_id: this.args.entry.id!,
    };

    const params = new URLSearchParams(queryParams);

    downloadUrl.search = params.toString();

    openUrl(downloadUrl.toString());
    onClose();
  }

  @action
  goBack(): void {
    this.stepIndex = Math.max(0, this.stepIndex - 1);
  }

  @action
  goNext(): void {
    this.stepIndex = Math.min(this.stepIndex + 1, this.steps.length - 1);
  }

  @action
  handleNextOrConfirmClick(): void {
    if (this.currentView === 'download-template') {
      this.downloadTemplate();
      return;
    }

    if (this.currentView === 'complete-upload') {
      if (!this.uploadOptions.file || !this.uploadOptions.isValid) {
        return;
      }

      this.usageAnalytics.track('Template Uploaded', {
        eventTrigger: 'upload-clicked',
      });
      void this.uploadTemplateTask.perform();
      return;
    }

    this.goNext();
  }

  @action
  onFileDrop(
    filesWithValidation: FileWithValidation[],
    isTooManyFiles: boolean,
  ): void {
    const [droppedFile] = filesWithValidation;

    if (droppedFile) {
      this.uploadOptions = {
        ...this.uploadOptions,
        file: droppedFile.file,
        isValid: droppedFile.validationErrors.length === 0,
        isTooManyFiles,
      };

      this.goNext();
    }
  }

  @action
  setMethod(method: Method): void {
    this.method = method;
  }

  @action
  updateDownloadOptions(options: Partial<DownloadOptions>): void {
    this.downloadOptions = {
      ...this.downloadOptions,
      ...options,
    };
  }

  @action
  updateUploadOptions(options: UploadOptions): void {
    this.uploadOptions = options;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.uploadTemplateTask.isRunning}}
      @onClose={{this.closeModal}}
      @title={{this.modalTitle}}
      class={{styles.modal}}
      data-test-id="upload-modal"
    >
      <:default>
        <div class="modal--padded modal--small flex flex-col gap-500">
          {{#if (eq this.currentView "select-method")}}
            <SettingsBudgetForecastUploadDownloadModalMethodSection
              @method={{this.method}}
              @setMethod={{this.setMethod}}
              @view={{@view}}
            />
          {{/if}}

          {{#if (eq this.currentView "download-template")}}
            <SettingsBudgetForecastUploadDownloadModalDownloadTemplate
              @downloadOptions={{this.downloadOptions}}
              @updateDownloadOptions={{this.updateDownloadOptions}}
              @view={{@view}}
            />
          {{/if}}

          {{#if (eq this.currentView "upload-file")}}
            <GeneralFileDropZone
              @allowedFormats={{this.allowedFormats}}
              @onFileDrop={{this.onFileDrop}}
              class="min-h-[240px]"
            />
          {{/if}}

          {{#if (eq this.currentView "complete-upload")}}
            <SettingsBudgetForecastUploadDownloadModalCompleteUpload
              @updateUploadOptions={{this.updateUploadOptions}}
              @uploadOptions={{this.uploadOptions}}
              @view={{@view}}
            />
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          {{#if (gt this.stepIndex 0)}}
            <PrismButton
              @buttonType="ghost"
              @iconBefore="arrow-left"
              @isDisabled={{this.uploadTemplateTask.isRunning}}
              @label={{t "general.back"}}
              @onClick={{this.goBack}}
              class="mr-auto"
            />
          {{/if}}
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.uploadTemplateTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.closeModal}}
            data-test-id="upload-modal-cancel"
          />
          <PrismButton
            @iconAfter={{this.buttonProperties.iconAfter}}
            @isDisabled={{this.buttonProperties.isDisabled}}
            @isLoading={{this.buttonProperties.isLoading}}
            @label={{this.buttonProperties.label}}
            @onClick={{this.handleNextOrConfirmClick}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
